import { useState } from "react";
import { useTableTemplateStore } from "../../store/tableTemplateStore";
import type { TableColumn } from "../../models/ManualAuthoring";

type Props = {
  column: TableColumn;
  level: number;
  parentHeader?: string;
};

function formatType(type?: TableColumn["type"]): string {
  if (!type) return "Text";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function formatWidth(width?: number): string {
  if (typeof width !== "number" || Number.isNaN(width)) return "Auto";
  return width > 100 ? `${width}px` : `${width}%`;
}

export default function ColumnTreeNode({ column, level, parentHeader }: Props) {
  const selected = useTableTemplateStore((s) => s.selectedColumnId);
  const selectColumn = useTableTemplateStore((s) => s.selectColumn);
  const addChild = useTableTemplateStore((s) => s.addChildColumn);
  const deleteColumn = useTableTemplateStore((s) => s.deleteColumn);
  const lockState = useTableTemplateStore((s) => s.lockState);

  const isSelected = selected === column.id;
  const isLocked = !!lockState?.isLocked;
  const hasChildren = !!column.columns?.length;
  const [isExpanded, setIsExpanded] = useState(true);

  const widthLabel = formatWidth(column.width);
  const childCount = column.columns?.length ?? 0;
  const summary = hasChildren
    ? `Grouped parent · Width: ${widthLabel} · ${childCount} child column${
        childCount === 1 ? "" : "s"
      }`
    : `Type: ${formatType(column.type)} · Width: ${widthLabel}`;
  const relation = parentHeader
    ? `Child column under ${parentHeader}`
    : "Standalone top-level column";
  const capacity =
    typeof column.width === "number" && !Number.isNaN(column.width)
      ? Math.max(8, Math.round(column.width))
      : 12;
  const capacityText = hasChildren
    ? `Estimated capacity: grouped header across ${widthLabel}`
    : `Estimated capacity: ~ ${capacity} characters`;
  const hintText = hasChildren
    ? "Parent uses colSpan"
    : typeof column.width === "number" && column.width < 12
      ? "Long text may wrap in PDF"
      : `Width: ${widthLabel}`;

  return (
    <div className="column-node-wrap" style={{ paddingLeft: level * 18 }}>
      <article
        className={`column-node-card ${isSelected ? "is-selected" : ""}`}
        onClick={() => selectColumn(column.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            selectColumn(column.id);
          }
        }}
      >
        <div className="column-node-head">
          <div className="column-node-title-block">
            <p className="column-node-title">{column.header || "Untitled Column"}</p>
            <p className="column-node-summary">{summary}</p>
          </div>

          <div className="column-node-actions">
            <button
              type="button"
              title={hasChildren ? (isExpanded ? "Collapse" : "Expand") : "No children"}
              onClick={(e) => {
                e.stopPropagation();
                if (!hasChildren) return;
                setIsExpanded((prev) => !prev);
              }}
              disabled={!hasChildren}
            >
              {isExpanded ? "←" : "→"}
            </button>
            <button
              type="button"
              title="Add child column"
              onClick={(e) => {
                e.stopPropagation();
                addChild(column.id);
              }}
              disabled={isLocked}
            >
              +
            </button>
            <button
              type="button"
              title="Delete column"
              onClick={(e) => {
                e.stopPropagation();
                deleteColumn(column.id);
                selectColumn(null);
              }}
              disabled={isLocked}
              className="column-node-delete"
            >
              x
            </button>
          </div>
        </div>

        <div className="column-node-meta">
          <p>{capacityText}</p>
          <p>{hintText}</p>
        </div>

        <div className="column-node-foot">
          <div className="column-node-required">
            {column.required ? "Required" : "Optional"}
          </div>
          <div className="column-node-relation">{relation}</div>
        </div>
      </article>

      {hasChildren && isExpanded ? (
        <div className="column-node-children">
          {column.columns?.map((child) => (
            <ColumnTreeNode
              key={child.id}
              column={child}
              level={level + 1}
              parentHeader={column.header || "Untitled Column"}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
